import * as React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Row } from '../../../layout/Row';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { ErrorText } from '../../../shared/form/ErrorMessage';
import { TextField } from '../../../shared/form/TextField';

export interface IAuthenticateProps {
  email: string;
  onPasswordChange: (value: string) => void;
  password: string;
  error?: string;
  onForgotPassword?: () => void;
}

export const Authenticate = React.memo(
  (props: IAuthenticateProps): React.ReactElement => {
    const errorId = 'password-error';

    return (
      <React.Fragment>
        <h1 id='password-heading' style={{ margin: '.5rem 1rem' }}>
          Enter Password
        </h1>
        <div style={{ margin: '.5rem 1rem' }} aria-live='polite'>
          Logging in as {props.email}
        </div>
        <InputContainer role='form' aria-labelledby='password-heading'>
          {/* Hidden username field for accessibility and password managers */}
          <input
            type='text'
            name='username'
            autoComplete='username'
            value={props.email}
            readOnly
            style={{ display: 'none' }}
            tabIndex={-1}
            aria-hidden='true'
          />
          <ControlSpacer>
            <TextField
              name='password'
              label='Password'
              type='password'
              style={{ width: '100%' }}
              value={props.password}
              onChange={props.onPasswordChange}
              role='textbox'
              autocomplete='current-password'
              aria-required='true'
              aria-invalid={Boolean(props.error)}
              aria-describedby={props.error ? errorId : undefined}
            />
          </ControlSpacer>
          {props.error && (
            <ErrorText
              id={errorId}
              style={{ marginTop: '0.5em' }}
              role='alert'
              aria-live='polite'
            >
              {props.error}
            </ErrorText>
          )}
          {props.onForgotPassword ? (
            <ForgotPasswordButton
              type='button'
              onClick={props.onForgotPassword}
              aria-label='Reset your password'
            >
              Forgot Password?
            </ForgotPasswordButton>
          ) : (
            <ForgotPasswordLink
              to={'/passwordreset'}
              aria-label='Reset your password'
            >
              Forgot Password?
            </ForgotPasswordLink>
          )}
        </InputContainer>
      </React.Fragment>
    );
  },
  (
    prevProps: Readonly<IAuthenticateProps>,
    nextProps: Readonly<IAuthenticateProps>
  ): boolean => {
    return (
      prevProps.email === nextProps.email &&
      prevProps.password === nextProps.password &&
      prevProps.error === nextProps.error &&
      prevProps.onPasswordChange === nextProps.onPasswordChange
    );
  }
);

Authenticate.displayName = 'Authenticate';

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 20px;
  padding-top: 0px;
  padding-bottom: 10px;
  width: 100%;
  height: 100%;
`;

const forgotPasswordStyles = `
  margin-top: 8px;
  padding: 6px 12px;
  color: #42a5f5;
  text-decoration: none;
  font-size: 12px;
  border: 1px solid #42a5f5;
  border-radius: 4px;
  text-align: center;
  align-self: flex-start;
  transition: all 200ms ease-in-out;
  background: transparent;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background-color: rgba(66, 165, 245, 0.1);
    text-decoration: none;
  }

  &:focus {
    outline: 2px solid #42a5f5;
    outline-offset: 2px;
  }
`;

const ForgotPasswordLink = styled(Link)`
  ${forgotPasswordStyles}
`;

const ForgotPasswordButton = styled.button`
  ${forgotPasswordStyles}
`;
